import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  useTheme, 
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  CardMembership as CardMembershipIcon
} from '@mui/icons-material';
import config from 'config';

// Import tab components
import MyPlanTab from './Components/MyPlanTab';
import HistoryTab from './Components/HistoryTab';
import SavedCardsTab from './Components/SavedCardsTab';

const Subscriptions = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1020px)');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get user data
  const { _id } = useSelector((state) => state.profile.user);
  const { userId } = useSelector((state) => state.main);
  const { isGuest } = useSelector((state) => state.main);  

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [hasPaid, setHasPaid] = useState(false);
  const [isPaymentExempt, setIsPaymentExempt] = useState(false);
  const [loading, setLoading] = useState(true);

  // Tab configuration
  const tabs = useMemo(() => [
    {
      label: 'My Plan',
      icon: <CreditCardIcon />,
      component: MyPlanTab,
      requiresPayment: true
    },
    {
      label: 'History',
      icon: <HistoryIcon />,
      component: HistoryTab,
      requiresPayment: true
    },
    {
      label: 'Saved Cards',
      icon: <CardMembershipIcon />,
      component: SavedCardsTab,
      requiresPayment: false
    }
  ], []);

  // Check subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const { createAxiosInstance } = await import('../../api');
        const { API_URL } = await import('../../api');
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/status/${userId}`);
        
        const { status } = response.data || {};
        const normalizedStatus = status || 'inactive';
        setSubscriptionStatus(normalizedStatus);
        
        // Check if user is payment exempt
        if (response.data?.paymentExempt) {
          setIsPaymentExempt(true);
          setHasPaid(true);
          return;
        }
        // Check if user has paid (active subscription)
        const paidStatuses = ['active', 'paused', 'past_due', 'canceled', 'cancelling'];
        setHasPaid(paidStatuses.includes(normalizedStatus));
        
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setSubscriptionStatus('inactive');
        setHasPaid(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [userId]);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Get current tab component
  const CurrentTabComponent = tabs[activeTab]?.component;

  // Check if current tab requires payment
  const currentTabRequiresPayment = tabs[activeTab]?.requiresPayment;
  const canAccessCurrentTab = !currentTabRequiresPayment || hasPaid;

  // Redirect to dashboard if no profile (but wait for loading to complete)
  useEffect(() => {
    if (!loading && !_id) {
      navigate('/dashboard');
      return;
    }
  }, [loading, _id, navigate]);

  // Auto-switch to Saved Cards tab if user doesn't have paid subscription and tries to access paid features
  useEffect(() => {
    if (!loading && currentTabRequiresPayment && !hasPaid) {
      setActiveTab(2); // Switch to Saved Cards tab (doesn't require payment)
    }
  }, [loading, currentTabRequiresPayment, hasPaid]);

  if (!loading && !_id) {
    return null; // Will redirect to dashboard
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: { xs: 1.5, sm: 2 }, 
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden'
    }}>
      {/* Page Title */}
      <Typography variant="h4" component="h1" sx={{ 
        mb: { xs: 2, sm: 3 }, 
        fontWeight: 600,
        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
        px: { xs: 2, sm: 0 }
      }}>
        Payment Plans
      </Typography>

      {/* Horizontal Tabs */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'white',
        borderRadius: 1,
        px: { xs: 0, sm: 2 },
        overflow: 'auto'
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: { xs: 2, sm: 3 }
            },
            '& .MuiTab-root': {
              minHeight: { xs: '44px', sm: '48px' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '0.9rem', md: '1rem' },
              px: { xs: 1.5, sm: 2 },
              '&.Mui-selected': {
                color: theme.palette.primary.main
              },
              '& .MuiSvgIcon-root': {
                fontSize: { xs: '1.25rem', sm: '1.5rem' }
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              disabled={tab.requiresPayment && !hasPaid}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: 'text.secondary'
                }
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderRadius: 1,
        minHeight: { xs: '300px', sm: '400px' },
        width: '100%',
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: { xs: '300px', sm: '400px' },
            gap: 2,
            p: { xs: 2, sm: 3 }
          }}>
            <CircularProgress size={{ xs: 32, sm: 40 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Loading subscription information...
            </Typography>
          </Box>
        ) : canAccessCurrentTab && CurrentTabComponent ? (
          <CurrentTabComponent
            subscriptionStatus={subscriptionStatus}
            hasPaid={hasPaid}
            isPaymentExempt={isPaymentExempt}
            userId={userId}
            isActive={true}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: { xs: '300px', sm: '400px' },
            gap: 2,
            p: { xs: 2, sm: 3 }
          }}>
            <CreditCardIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1.15rem', sm: '1.25rem' } }}>
              Plan required
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              You need an active payment plan to access this feature.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

Subscriptions.displayName = 'Subscriptions';

export default Subscriptions;
